import {
  ColumnsType,
  FloatingFormItem,
  InfiniteTable,
  InputDecimal,
} from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import GenerateInvoiceButtons from '@/components/Movements/PurchaseReturn/GenerateInvoiceButtons';
import { Purchase } from '@/core/domain/purchase/Purchase.types';
import {
  useItemsPurchaseReturnGenerateInvoice,
  useItemsPurchasesReturn,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import {
  PurchaseItem,
  ReturnItemFormValues,
  ReturnItemGenerateInvoice,
  UpdateIpiValueInInvoiceItemEnum,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { useDisclosure } from '@/hooks';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { getTableScrollX } from '@/utils/table';
import {
  Button,
  ButtonProps,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Modal,
  Select,
  Typography,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';

interface FormType extends ReturnItemFormValues {
  generateReferencedDocument: boolean;
  updateIpiValueInInvoiceItem: UpdateIpiValueInInvoiceItemEnum;
}

interface Props extends ButtonProps {
  purchase: Purchase;
}

export default function PurchasesButtonsPurchaseReturn({
  purchase,
  ...props
}: Props) {
  const [form] = Form.useForm<FormType>();
  const items = Form.useWatch('items', form);

  const [modalVisible, modalAction] = useDisclosure();
  const [hasKey, setHasKey] = useState<boolean>(false);
  const [rowKeys, setRowKeys] = useState<number[]>([]);
  const [generateInvoiceId, setGenerateInvoiceId] = useState<number>(0);
  const generateInvoice = useItemsPurchaseReturnGenerateInvoice();

  const { data, isFetching, fetchNextPage, hasNextPage, dataUpdatedAt } =
    useItemsPurchasesReturn(
      {
        purchaseId: purchase.id,
        companyId: purchase.companyId,
        supplierId: purchase.supplierDTO.id,
      },
      modalVisible,
      false,
    );

  const itemsPurchaseReturn = flatInfiniteData(data);

  useEffect(() => {
    if (itemsPurchaseReturn.length) {
      form.setFieldValue('items', itemsPurchaseReturn);
    } else {
      form.setFieldValue('items', []);
    }
    setRowKeys([]);
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (items?.length) {
      const fieldItems = items.filter(
        (item: PurchaseItem) => typeof item?.quantityToReturn === 'number',
      );

      setHasKey(fieldItems.some(({ hasKey }: PurchaseItem) => hasKey));
      setRowKeys(fieldItems.map((item: PurchaseItem) => item.id));
    }
  }, [items]);

  const handleGenerateInvoice = async () => {
    const values = await form.validateFields();

    const purchaseItems: ReturnItemGenerateInvoice[] = values.items
      ?.filter((item) => Number.isFinite(item?.quantityToReturn))
      ?.map((item) => ({
        ...item,
        keyNfe: item.purchase?.keyNfe,
        purchase: item.purchase?.id,
        purchaseCode: item.purchase?.code,
      }));

    const response = await generateInvoice.mutateAsync({
      generateReferencedDocument: values.generateReferencedDocument,
      updateIpiValueInInvoiceItem:
        values.updateIpiValueInInvoiceItem ||
        UpdateIpiValueInInvoiceItemEnum.NONE_OPTIONS,
      purchaseItemsRequestDTO: purchaseItems,
    });

    setGenerateInvoiceId(response.invoiceId);
    form.resetFields([
      'generateReferencedDocument',
      'updateIpiValueInInvoiceItem',
    ]);
  };

  const columns: ColumnsType<PurchaseItem> = [
    {
      key: 'purchaseDate',
      dataIndex: ['purchase', 'releaseDate'],
      title: 'Data Compra',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      ellipsis: true,
      width: 100,
    },
    useConfigurationColumnsPart({
      internalCode: 'sku',
      manufacturingCode: 'manufacturingCode',
      barcode: 'barCode',
      companyCode: 'companyCode',
    }),
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Descrição',
      ellipsis: true,
      width: 210,
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: '	Qtde. Comprada',
      render: (value) => quantityFormatter(value || 0),
      ellipsis: true,
      width: 120,
    },
    {
      key: 'amountReturned',
      dataIndex: 'amountReturned',
      title: 'Qtde. Devolvida',
      render: (value) => value ?? 0,
      ellipsis: true,
      width: 120,
    },
    {
      key: 'quantityToReturn',
      title: 'Qtde. a Devolver',
      render(_, row, index: number) {
        return (
          <Form.Item
            style={{ margin: 0 }}
            name={[index, 'quantityToReturn']}
            rules={[
              {
                required: rowKeys.includes(row.id),
                message: 'Preencha o campo',
              },
              {
                type: 'number',
                min: 0.01,
                message: 'Preencha um número maior que 0!',
              },
            ]}
          >
            <InputDecimal max={row.quantity - (row.amountReturned ?? 0)} />
          </Form.Item>
        );
      },
      width: 120,
      fixed: 'right',
      align: 'right',
    },
  ];

  return (
    <>
      <Button
        block
        type="primary"
        onClick={modalAction.open}
        disabled={!itemsPurchaseReturn.length}
        loading={generateInvoice.isLoading}
        {...props}
      >
        Devolução da compra
      </Button>

      <Modal
        title="Devolução da Compra"
        open={modalVisible}
        onCancel={() => modalAction.close()}
        width={'70%'}
        key="modalAuthorize"
        footer={
          <SpaceForButton justifyContent="end">
            <Button
              block
              type="primary"
              ghost
              onClick={() => modalAction.close()}
            >
              Cancelar
            </Button>

            <Button
              block
              type="primary"
              disabled={!rowKeys.length || !!generateInvoiceId}
              loading={generateInvoice.isLoading}
              onClick={() => handleGenerateInvoice()}
            >
              Gerar nota fiscal
            </Button>
          </SpaceForButton>
        }
      >
        <Form form={form}>
          {!!generateInvoiceId && (
            <Col
              span={24}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <GenerateInvoiceButtons
                generateInvoiceId={generateInvoiceId}
                setGenerateInvoiceId={setGenerateInvoiceId}
              />
            </Col>
          )}

          {!generateInvoiceId && (
            <Form.List name="items" initialValue={[]}>
              {() => {
                if (itemsPurchaseReturn?.length) {
                  return (
                    <InfiniteTable<PurchaseItem>
                      rowSelection={{
                        onChange: (rowKeys, selecteds) => {
                          setRowKeys(rowKeys as number[]);
                          setHasKey(selecteds.some(({ hasKey }) => hasKey));
                          form.setFieldValue(
                            'items',
                            form
                              .getFieldValue('items')
                              .map((item: PurchaseItem) =>
                                !rowKeys.includes(item.id)
                                  ? {
                                      ...item,
                                      quantityToReturn: undefined,
                                    }
                                  : {
                                      ...item,
                                      quantityToReturn:
                                        (item.quantity ?? 0) -
                                        (item.amountReturned ?? 0),
                                    },
                              ),
                          );
                        },
                        selectedRowKeys: rowKeys,
                      }}
                      dataSource={itemsPurchaseReturn}
                      loading={isFetching}
                      columns={columns}
                      rowKey={(row) => row.id}
                      hasNextPage={hasNextPage}
                      fetchNextPage={fetchNextPage}
                      scroll={{ x: getTableScrollX(columns) }}
                    />
                  );
                } else {
                  return (
                    <>
                      <Divider />

                      <Empty description="Não foi encontrado itens para realizar a devolução" />
                    </>
                  );
                }
              }}
            </Form.List>
          )}

          {!generateInvoiceId && (
            <>
              <Divider />

              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong>
                  Para os itens que possuem valor do IPI, selecione como o IPI
                  deve ser tratado na nota fiscal:
                </Typography.Text>
              </div>

              <FloatingFormItem name="updateIpiValueInInvoiceItem">
                <Select
                  allowClear
                  disabled={!rowKeys.length}
                  options={[
                    {
                      label: 'Não incluir IPI',
                      value: UpdateIpiValueInInvoiceItemEnum.NONE_OPTIONS,
                    },
                    {
                      label: 'Manter como valor do IPI',
                      value: UpdateIpiValueInInvoiceItemEnum.IPI_NORMAL,
                    },
                    {
                      label: ' Manter como valor do IPI devolvido',
                      value: UpdateIpiValueInInvoiceItemEnum.IPI_RETURNED,
                    },
                  ]}
                />
              </FloatingFormItem>

              {hasKey && (
                <FloatingFormItem
                  name="generateReferencedDocument"
                  initialValue={true}
                  valuePropName="checked"
                  style={{ marginTop: '16px' }}
                >
                  <Checkbox>
                    Incluir documento referenciado na nota fiscal de devolução?
                  </Checkbox>
                </FloatingFormItem>
              )}
            </>
          )}
        </Form>
      </Modal>
    </>
  );
}
